import { z } from "zod"
import { Provider } from "./channels.js"

/** Campaigns (screens 24–29, 52). One channel, one approved message, one frozen audience snapshot. */

export const CampaignStatus = z.enum(["draft", "scheduled", "sending", "attention", "completed", "cancelled"])
export type CampaignStatus = z.infer<typeof CampaignStatus>

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  draft: "Draft",
  scheduled: "Scheduled",
  sending: "Sending",
  attention: "Needs attention",
  completed: "Completed",
  cancelled: "Cancelled",
}

export const ExclusionReason = z.enum(["no_identity", "no_permission", "opted_out", "window_closed"])
export type ExclusionReason = z.infer<typeof ExclusionReason>

export const RecipientReason = z.enum(["no_identity", "no_permission", "opted_out", "window_closed", "human_owned", "channel_disconnected", "cancelled", "permission_revoked", "identity_removed"])
export type RecipientReason = z.infer<typeof RecipientReason>

export const RECIPIENT_REASON_LABELS: Record<RecipientReason, string> = {
  no_identity: "Channel identity unavailable",
  no_permission: "Marketing permission missing",
  opted_out: "Opted out",
  window_closed: "Messaging window closed",
  human_owned: "A person owns this conversation",
  channel_disconnected: "Channel disconnected",
  cancelled: "Campaign cancelled",
  permission_revoked: "Marketing permission revoked at send",
  identity_removed: "Channel identity no longer available",
}

/** What the recipient screen shows: preparation states, then ledger-derived submission and delivery. */
export const RecipientOutcome = z.enum(["excluded", "eligible", "skipped", "queued", "awaiting_receipt", "delivered", "failed", "unconfirmed", "cancelled"])
export type RecipientOutcome = z.infer<typeof RecipientOutcome>

export const RECIPIENT_OUTCOME_LABELS: Record<RecipientOutcome, string> = {
  excluded: "Excluded",
  eligible: "Eligible",
  skipped: "Skipped",
  queued: "Queued",
  awaiting_receipt: "Awaiting receipt",
  delivered: "Delivered",
  failed: "Failed",
  unconfirmed: "Unconfirmed",
  cancelled: "Cancelled",
}

export const CampaignButton = z.object({ label: z.string().trim().min(1).max(24), url: z.url().regex(/^https:\/\//, "The destination must use HTTPS") })
export type CampaignButton = z.infer<typeof CampaignButton>

export const AudienceEstimate = z.object({
  selected: z.number().int(),
  eligible: z.number().int(),
  excluded: z.number().int(),
  exclusions: z.record(ExclusionReason, z.number().int()),
  estimatedAt: z.iso.datetime(),
})
export type AudienceEstimate = z.infer<typeof AudienceEstimate>

export const CampaignSummary = z.object({
  id: z.uuid(),
  name: z.string(),
  status: CampaignStatus,
  provider: Provider.nullable(),
  connectionLabel: z.string().nullable(),
  segmentName: z.string().nullable(),
  /** Audience or timing, as the list shows it. */
  detail: z.string(),
  attentionReason: z.string().nullable(),
  scheduledAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime(),
})
export type CampaignSummary = z.infer<typeof CampaignSummary>

export const CampaignsPage = z.object({
  campaigns: z.array(CampaignSummary),
  metrics: z.object({ drafts: z.number().int(), scheduled: z.number().int(), nextSendAt: z.iso.datetime().nullable(), deliveredThisMonth: z.number().int(), attention: z.number().int() }),
})
export type CampaignsPage = z.infer<typeof CampaignsPage>

export const CampaignDetail = z.object({
  id: z.uuid(),
  name: z.string(),
  status: CampaignStatus,
  provider: Provider.nullable(),
  connectionId: z.uuid().nullable(),
  connectionLabel: z.string().nullable(),
  segmentId: z.uuid().nullable(),
  segmentName: z.string().nullable(),
  segmentSummary: z.string().nullable(),
  segmentCount: z.number().int().nullable(),
  message: z.object({ text: z.string(), button: CampaignButton.nullable(), versionNumber: z.number().int().nullable() }),
  timing: z.enum(["now", "scheduled"]),
  scheduledAt: z.iso.datetime().nullable(),
  timezone: z.string(),
  approvedAt: z.iso.datetime().nullable(),
  approvedByName: z.string().nullable(),
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  attentionReason: z.string().nullable(),
  snapshot: z
    .object({ status: z.enum(["building", "ready", "failed"]), cutoffAt: z.iso.datetime(), selected: z.number().int(), eligible: z.number().int(), excluded: z.number().int(), exclusions: z.record(ExclusionReason, z.number().int()) })
    .nullable(),
  updatedAt: z.iso.datetime(),
})
export type CampaignDetail = z.infer<typeof CampaignDetail>

export const CreateCampaignInput = z.object({ name: z.string().trim().min(1).max(120), segmentId: z.uuid().optional(), connectionId: z.uuid().optional() })
export type CreateCampaignInput = z.infer<typeof CreateCampaignInput>

export const UpdateCampaignInput = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  segmentId: z.uuid().nullable().optional(),
  connectionId: z.uuid().nullable().optional(),
  message: z.object({ text: z.string().max(1000), button: CampaignButton.nullable() }).optional(),
  schedule: z
    .object({ timing: z.enum(["now", "scheduled"]), date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(), time: z.string().regex(/^\d{2}:\d{2}$/).optional(), timezone: z.string().min(1) })
    .optional(),
})
export type UpdateCampaignInput = z.infer<typeof UpdateCampaignInput>

export const ApproveCampaignInput = z.object({ reviewed: z.literal(true) })
export type ApproveCampaignInput = z.infer<typeof ApproveCampaignInput>

export const CampaignResults = z.object({
  campaign: CampaignDetail,
  funnel: z.object({ selected: z.number().int(), eligible: z.number().int(), accepted: z.number().int(), delivered: z.number().int(), replied: z.number().int() }),
  outcomes: z.object({ delivered: z.number().int(), awaitingReceipt: z.number().int(), failed: z.number().int(), skipped: z.number().int(), unconfirmed: z.number().int(), queued: z.number().int(), cancelled: z.number().int() }),
  excluded: z.number().int(),
  updatedAt: z.iso.datetime(),
})
export type CampaignResults = z.infer<typeof CampaignResults>

export const CampaignRecipient = z.object({
  id: z.uuid(),
  contactId: z.uuid(),
  contactName: z.string(),
  handle: z.string().nullable(),
  outcome: RecipientOutcome,
  evidence: z.string(),
  conversationId: z.uuid().nullable(),
  updatedAt: z.iso.datetime(),
})
export type CampaignRecipient = z.infer<typeof CampaignRecipient>

export const RecipientsQuery = z.object({ outcome: RecipientOutcome.optional(), q: z.string().max(120).optional() })
export type RecipientsQuery = z.infer<typeof RecipientsQuery>

export const RecipientsPage = z.object({
  recipients: z.array(CampaignRecipient),
  counts: z.record(RecipientOutcome, z.number().int()),
  total: z.number().int(),
})
export type RecipientsPage = z.infer<typeof RecipientsPage>
